import { reactive, watch } from 'vue';

const DICTIONARY = {
  en: {
    // Navigation
    home: "Home",
    skills: "Skills",
    academics: "Academics",
    progress: "Progress",
    profile: "Profile",
    logout: "Logout",
    readyToPlay: "Ready to Play",

    // Home Page Hero
    heroTitle: "Interactive Learning Platform",
    heroSubtitle: "Master technical skills and school academics with high-fidelity interactive lessons",
    startChessBtn: "Start Chess",
    learnSingingBtn: "Learn Singing",
    
    // Portal Sections
    skillsHub: "🌌 Skills Learning Hub",
    skillsHubSub: "Master real-time interactive skills: tactical chess vs balanced AI Coach, computer-vision calibrated yoga poses, and singing with pitch-tracking analytics.",
    academicsHub: "🏫 Academic Mastery Center",
    academicsHubSub: "Excel in your school curriculum. Comprehensive English literature and Mathematics resources specifically built for school students of Grades 5 to 10.",
    
    // Exploration Buttons
    exploreSkills: "Explore Skills Hub",
    exploreAcademics: "Explore Academics Hub",
    
    // Skill Cards
    chessPuzzlesTitle: "Chess Puzzles",
    chessPuzzlesDesc: "Solve chess puzzles from basics to advanced strategies with interactive feedback.",
    multiChessTitle: "Multiplayer Chess & AI Coach",
    multiChessDesc: "Play vs others online or challenge the Gemini-powered AI Coach with take-backs and balanced play.",
    yogaTitle: "Yoga Pose Training",
    yogaTitleGame: "Yoga AI Game",
    yogaDesc: "Calibrate your poses in real-time with pose detection and practice routines.",
    singingTitle: "Singing Practice",
    singingDesc: "Sing along to lyrics while your microphone tracks your pitch and rhythm in real-time.",
    vocalsTitle: "Vocal Notes Training",
    vocalsDesc: "Practice keeping single pitches with a real-time responsive pitch visualizer.",
    progressTitle: "Progress Tracker",
    progressDesc: "Monitor your overall learning statistics and view performance analytics.",

    // Academic Cards
    englishTitle: "English Literature & Grammar",
    englishDesc: "Learn key textual devices, Simile, Metaphor, Imagery, and analyze their effects with practice writing.",
    mathsTitle: "Mathematics & Logic",
    mathsDesc: "Excel in algebra, geometry, ratios, decimals, and view interactive visual math simulators.",
    gradeSelect: "Select Grade Level",
    subjectSelect: "Choose Subject",
    allGrades: "Grades 5 - 10",

    // Grades
    grade5: "Grade 5",
    grade6: "Grade 6",
    grade7: "Grade 7",
    grade8: "Grade 8",
    grade9: "Grade 9",
    grade10: "Grade 10",
    grade11: "Grade 11",
    grade12: "Grade 12",
    
    // Academics Main page
    welcomeAcademics: "Welcome to the Academic Hub!",
    academicsSub: "Choose a subject and select your grade level to access visual lessons, formulas, and mock practices.",
    englishHubTitle: "English Masterclass",
    englishHubDesc: "Understand textual devices, hyperbole, metonymy, literary tools, and write your own devices.",
    mathsHubTitle: "Mathematics Studio",
    mathsHubDesc: "Explore geometry visualizers, fractional pie charts, ratio charts, and interactive formula carousels.",
    startLearning: "Start Learning",
    
    // Textual Devices Keys
    definition: "Definition",
    purposeEffect: "Purpose & Effect",
    howToUse: "How to Use It",
    example: "Example",
    tryItOut: "Interactive Writing Sandbox",
    quizHeader: "Test Your Knowledge",
    correct: "Correct!",
    incorrect: "Incorrect. Try again!",
    score: "Score",

    // Navigation (new)
    learn: "Learn",
    arena: "Arena",
    leaderboard: "Leaderboard",
    settings: "Settings",
    instruments: "Instruments",
    instrumentsDesc: "Jump straight into Song Practice, then warm up on the virtual harmonium and meet six instruments of India.",
    science: "Science",
    maths: "Maths",
    english: "English",
    chess: "Chess",
    yoga: "Yoga",
    singing: "Singing",

    // Gamification
    xp: "XP",
    level: "Level",
    dayStreak: "day streak",
    coins: "Coins",
    dailyGoal: "Daily Goal",
    goalReached: "Daily goal reached! 🎉",
    keepGoing: "Keep going!",
    levelUp: "Level Up!",
    playNow: "Play Now",
    yourRank: "Your Rank",
    points: "points",

    // Quiz Arena
    quizArena: "Quiz Arena",
    arenaTagline: "Answer fast, score big, beat everyone!",
    soloSprint: "Solo Sprint",
    soloSprintDesc: "60 seconds. How many can you solve?",
    liveArena: "Live Arena",
    liveArenaDesc: "Race against other players — fastest answer wins!",
    nextRoundIn: "Next round starts in",
    roundEndsIn: "Round ends in",
    waitingPlayers: "Players in this round",
    typeAnswer: "Type your answer…",
    submit: "Submit",
    skip: "Skip",
    combo: "Combo",
    finalScore: "Final Score",
    correctAnswers: "Correct",
    playAgain: "Play Again",
    backToHome: "Back to Home",
    joinRound: "Join Round",
    getReady: "Get ready…",
    go: "GO!",
    youWon: "You won the round! 🏆",
    roundOver: "Round Over",

    // Settings
    language: "Language",
    bigText: "Large Text",
    bigTextDesc: "Bigger letters everywhere",
    soundEffects: "Sounds",

    // Subjects hub
    chooseSubject: "What do you want to learn today?",
    continueLearning: "Continue Learning",
    welcomeBack: "Welcome back",
    welcomeNew: "Welcome",

    // Singing extras
    chooseSong: "Choose a Song",
    easySong: "Easy",
    mediumSong: "Medium",
    hardSong: "Hard",
    withVideo: "With Video",
    practiceMode: "Practice Mode"
  },
  hi: {
    // Navigation
    home: "मुख्य पृष्ठ",
    skills: "कौशल",
    academics: "अकादमिक",
    progress: "प्रगति",
    profile: "प्रोफ़ाइल",
    logout: "लॉगआउट",
    readyToPlay: "खेलने के लिए तैयार",

    // Home Page Hero
    heroTitle: "इंटरैक्टिव लर्निंग प्लेटफॉर्म",
    heroSubtitle: "उच्च गुणवत्ता वाले इंटरैक्टिव पाठों के साथ तकनीकी कौशल और स्कूली शिक्षा में महारत हासिल करें",
    startChessBtn: "शतरंज शुरू करें",
    learnSingingBtn: "गायन सीखें",
    
    // Portal Sections
    skillsHub: "🌌 कौशल शिक्षण केंद्र",
    skillsHubSub: "वास्तविक समय के इंटरैक्टिव कौशल सीखें: संतुलित एआई कोच के खिलाफ शतरंज, कंप्यूटर विज़न द्वारा संरेखित योग मुद्राएं, और पिच-ट्रैकिंग विश्लेषण के साथ गायन।",
    academicsHub: "🏫 शैक्षणिक उत्कृष्टता केंद्र",
    academicsHubSub: "अपने स्कूली पाठ्यक्रम में उत्कृष्ट प्रदर्शन करें। कक्षा 5 से 10 तक के छात्रों के लिए विशेष रूप से तैयार अंग्रेजी साहित्य और गणित के विस्तृत संसाधन।",
    
    // Exploration Buttons
    exploreSkills: "कौशल केंद्र में प्रवेश करें",
    exploreAcademics: "शैक्षणिक केंद्र में प्रवेश करें",
    
    // Skill Cards
    chessPuzzlesTitle: "शतरंज पहेलियाँ",
    chessPuzzlesDesc: "इंटरैक्टिव फीडबैक के साथ बुनियादी से लेकर उन्नत रणनीतियों तक की शतरंज की पहेलियां हल करें।",
    multiChessTitle: "मल्टीप्लेयर शतरंज और एआई कोच",
    multiChessDesc: "ऑनलाइन दूसरों के खिलाफ खेलें या जेमिनी-संचालित एआई कोच को चुनौती दें जिसमें चाल वापस लेने और संतुलित खेल की सुविधा है।",
    yogaTitle: "योग मुद्रा प्रशिक्षण",
    yogaTitleGame: "योग एआई गेम",
    yogaDesc: "वास्तविक समय में मुद्रा संसूचन और अभ्यास दिनचर्या के साथ अपनी मुद्राओं को सही करें।",
    singingTitle: "गायन अभ्यास",
    singingDesc: "गीतों के साथ गाएं जब आपका माइक्रोफ़ोन वास्तविक समय में आपकी पिच और लय को ट्रैक करता है।",
    vocalsTitle: "स्वर नोट्स प्रशिक्षण",
    vocalsDesc: "वास्तविक समय के संवेदनशील पिच विज़ुअलाइज़र के साथ सिंगल पिच बनाए रखने का अभ्यास करें।",
    progressTitle: "प्रगति ट्रैकर",
    progressDesc: "अपने समग्र शिक्षण आंकड़ों की निगरानी करें और प्रदर्शन विश्लेषण देखें।",

    // Academic Cards
    englishTitle: "अंग्रेजी साहित्य और व्याकरण",
    englishDesc: "प्रमुख पाठ्य उपकरणों (उपमा, रूपक, दृश्य-चित्रण) को सीखें और लेखन अभ्यास के साथ उनके प्रभावों का विश्लेषण करें।",
    mathsTitle: "गणित और तर्क",
    mathsDesc: "बीजगणित, ज्यामिति, अनुपात, दशमलव में उत्कृष्ट प्रदर्शन करें और इंटरैक्टिव दृश्य गणित सिमुलेटर देखें।",
    gradeSelect: "कक्षा स्तर चुनें",
    subjectSelect: "विषय चुनें",
    allGrades: "कक्षा 5 - 10",

    // Grades
    grade5: "कक्षा 5",
    grade6: "कक्षा 6",
    grade7: "कक्षा 7",
    grade8: "कक्षा 8",
    grade9: "कक्षा 9",
    grade10: "कक्षा 10",
    grade11: "कक्षा 11",
    grade12: "कक्षा 12",
    
    // Academics Main page
    welcomeAcademics: "अकादमिक हब में आपका स्वागत है!",
    academicsSub: "दृश्य पाठों, सूत्रों और अभ्यास परीक्षाओं तक पहुंचने के लिए एक विषय चुनें और अपना कक्षा स्तर चुनें।",
    englishHubTitle: "अंग्रेजी मास्टरक्लास",
    englishHubDesc: "पाठ्य उपकरणों, अतिशयोक्ति, रूपक, साहित्यिक उपकरणों को समझें और अपने स्वयं के उपकरण लिखें।",
    mathsHubTitle: "गणित स्टूडियो",
    mathsHubDesc: "ज्यामिति विज़ुअलाइज़र, आंशिक पाई चार्ट, अनुपात चार्ट और इंटरैक्टिव फ़ॉर्मूला कैरोसेल का अन्वेषण करें।",
    startLearning: "सीखना शुरू करें",
    
    // Textual Devices Keys
    definition: "परिभाषा",
    purposeEffect: "उद्देश्य और प्रभाव",
    howToUse: "इसका उपयोग कैसे करें",
    example: "उदाहरण",
    tryItOut: "इंटरैक्टिव राइटिंग सैंडबॉक्स",
    quizHeader: "अपने ज्ञान का परीक्षण करें",
    correct: "सही उत्तर!",
    incorrect: "गलत उत्तर। फिर से प्रयास करें!",
    score: "स्कोर",

    // Navigation (new)
    learn: "सीखें",
    arena: "अखाड़ा",
    leaderboard: "लीडरबोर्ड",
    settings: "सेटिंग्स",
    instruments: "वाद्य यंत्र",
    instrumentsDesc: "सीधे गीत अभ्यास में जाएँ, फिर वर्चुअल हारमोनियम पर वार्म-अप करें और भारत के छह वाद्य यंत्रों से मिलें।",
    science: "विज्ञान",
    maths: "गणित",
    english: "अंग्रेज़ी",
    chess: "शतरंज",
    yoga: "योग",
    singing: "गायन",

    // Gamification
    xp: "XP",
    level: "स्तर",
    dayStreak: "दिन की लगन",
    coins: "सिक्के",
    dailyGoal: "आज का लक्ष्य",
    goalReached: "आज का लक्ष्य पूरा! 🎉",
    keepGoing: "जारी रखें!",
    levelUp: "स्तर बढ़ा!",
    playNow: "अभी खेलें",
    yourRank: "आपकी रैंक",
    points: "अंक",

    // Quiz Arena
    quizArena: "क्विज़ अखाड़ा",
    arenaTagline: "तेज़ी से जवाब दो, बड़ा स्कोर बनाओ, सबको हराओ!",
    soloSprint: "सोलो स्प्रिंट",
    soloSprintDesc: "60 सेकंड। आप कितने सवाल हल कर सकते हैं?",
    liveArena: "लाइव अखाड़ा",
    liveArenaDesc: "दूसरे खिलाड़ियों से मुकाबला — सबसे तेज़ जवाब जीतता है!",
    nextRoundIn: "अगला राउंड शुरू होगा",
    roundEndsIn: "राउंड खत्म होगा",
    waitingPlayers: "इस राउंड के खिलाड़ी",
    typeAnswer: "अपना जवाब लिखें…",
    submit: "जमा करें",
    skip: "छोड़ें",
    combo: "कॉम्बो",
    finalScore: "अंतिम स्कोर",
    correctAnswers: "सही",
    playAgain: "फिर से खेलें",
    backToHome: "मुख्य पृष्ठ पर जाएँ",
    joinRound: "राउंड में शामिल हों",
    getReady: "तैयार हो जाओ…",
    go: "शुरू!",
    youWon: "आप राउंड जीत गए! 🏆",
    roundOver: "राउंड समाप्त",

    // Settings
    language: "भाषा",
    bigText: "बड़े अक्षर",
    bigTextDesc: "हर जगह बड़े अक्षर",
    soundEffects: "आवाज़ें",

    // Subjects hub
    chooseSubject: "आज आप क्या सीखना चाहते हैं?",
    continueLearning: "सीखना जारी रखें",
    welcomeBack: "वापसी पर स्वागत है",
    welcomeNew: "स्वागत है",

    // Singing extras
    chooseSong: "गाना चुनें",
    easySong: "आसान",
    mediumSong: "मध्यम",
    hardSong: "कठिन",
    withVideo: "वीडियो के साथ",
    practiceMode: "अभ्यास मोड"
  }
};

const state = reactive({
  currentLanguage: localStorage.getItem('siteLanguage') || 'en'
});

watch(() => state.currentLanguage, (newLang) => {
  localStorage.setItem('siteLanguage', newLang);
});

export const languageState = state;

export function t(key) {
  const lang = state.currentLanguage;
  if (DICTIONARY[lang] && DICTIONARY[lang][key]) {
    return DICTIONARY[lang][key];
  }
  // Fallback to English key or key name itself
  if (DICTIONARY.en && DICTIONARY.en[key]) {
    return DICTIONARY.en[key];
  }
  return key;
}

export function setLanguage(lang) {
  if (lang === 'en' || lang === 'hi') {
    state.currentLanguage = lang;
  }
}
